import React, { useState } from 'react';
import { GraduationCap, CheckCircle2, Clock, Plus, Target, Award, ShieldCheck, FileCheck, UserCheck } from 'lucide-react';
import { DEMO_STUDENTS } from '../../data/demoStudents';

export const StudentIEPModule: React.FC = () => {
  const [selectedStudentId, setSelectedStudentId] = useState(DEMO_STUDENTS[0].id);
  const [udidNumber, setUdidNumber] = useState('MH-27-00000-2026-1234567');
  const [disabilityPercent, setDisabilityPercent] = useState(65);
  const [rpwdCategory, setRpwdCategory] = useState('Autism Spectrum Disorder (ASD)');

  const activeStudent = DEMO_STUDENTS.find(s => s.id === selectedStudentId) || DEMO_STUDENTS[0];

  const [goals, setGoals] = useState([
    { id: '1', title: 'Independent AAC Sentence Formation (4+ words)', domain: 'Communication', progress: 75, targetDate: 'Dec 2026' },
    { id: '2', title: 'Fine Motor Pencil Grip & Tactile Cell Recognition', domain: 'Occupational Therapy', progress: 60, targetDate: 'Nov 2026' },
    { id: '3', title: 'Peer Group Social Interaction & Token Rewards', domain: 'Behavioral / Social', progress: 85, targetDate: 'Oct 2026' },
  ]);

  const rpwd21Categories = [
    'Autism Spectrum Disorder (ASD)',
    'Speech & Language Disability',
    'Locomotor Disability / Cerebral Palsy',
    'Hearing Impairment (Deaf & Hard of Hearing)',
    'Visual Impairment / Blindness / Low Vision',
    'Intellectual Disability',
    'Specific Learning Disabilities (SLD)',
    'Mental Illness',
    'Multiple Disabilities',
    'Chronic Neurological Conditions',
    'Muscular Dystrophy',
    'Dwarfism'
  ];

  const handleUpdateProgress = (id: string, delta: number) => {
    setGoals(goals.map(g => {
      if (g.id === id) {
        const next = Math.min(100, Math.max(0, g.progress + delta));
        return { ...g, progress: next };
      }
      return g;
    }));
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-6">
      
      {/* Demo Student Selector Header */}
      <div className="bg-emerald-50 border border-emerald-200 p-3 rounded-xl flex flex-col md:flex-row items-center justify-between gap-3 text-xs">
        <div className="flex items-center gap-2">
          <UserCheck className="w-4 h-4 text-emerald-700 shrink-0" />
          <span className="font-bold text-slate-800">Selected IEP Student:</span>
          <select 
            value={selectedStudentId}
            onChange={(e) => setSelectedStudentId(e.target.value)}
            className="px-2.5 py-1 bg-white border border-emerald-300 rounded-lg font-bold text-emerald-900 focus:ring-2 focus:ring-emerald-500"
          >
            {DEMO_STUDENTS.map(st => (
              <option key={st.id} value={st.id}>{st.name} ({st.rollId}) - {st.disabilityCategory}</option>
            ))}
          </select>
        </div>

        <div className="text-[11px] text-emerald-800 font-semibold truncate">
          Assigned SLP/OT: <span className="font-bold">{activeStudent.therapist}</span>
        </div>
      </div>

      <div className="flex items-center justify-between border-b border-slate-100 pb-4">
        <div>
          <span className="px-2.5 py-0.5 bg-emerald-100 text-emerald-800 text-[10px] font-bold rounded-full uppercase tracking-wider flex items-center gap-1 w-fit">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
            RPwD Act 2016 & IDEA 300.320 Certified Standard
          </span>
          <h2 className="text-2xl font-bold text-slate-900 mt-1">Student Profile & SMART IEP Goals</h2>
          <p className="text-xs text-slate-500">Individualized Education Program milestones, accommodations, and progress tracking</p>
        </div>
      </div>

      {/* Govt of India UDID & RPwD Compliance Card */}
      <div className="bg-slate-50 border border-slate-200 p-4 rounded-xl space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-xs font-extrabold text-slate-800 flex items-center gap-2">
            <FileCheck className="w-4 h-4 text-blue-600" />
            Government of India UDID Card & RPwD Act 2016 Category Verification
          </div>
          <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-300 rounded text-[10px] font-extrabold">
            UDID Verified (40%+ Benchmark Threshold)
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
          <div>
            <label className="block text-[10px] font-bold text-slate-500 mb-1">Official 18-Digit UDID Card No.</label>
            <input
              type="text"
              value={udidNumber}
              onChange={(e) => setUdidNumber(e.target.value)}
              className="w-full px-3 py-1.5 border border-slate-300 rounded-lg font-mono font-bold text-slate-900 focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <div>
            <label className="block text-[10px] font-bold text-slate-500 mb-1">Disability Percentage (% Threshold)</label>
            <div className="flex items-center gap-2">
              <input
                type="range" min="40" max="100"
                value={disabilityPercent}
                onChange={(e) => setDisabilityPercent(parseInt(e.target.value))}
                className="flex-1 accent-emerald-600"
              />
              <span className="font-mono font-extrabold text-emerald-700">{disabilityPercent}%</span>
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-bold text-slate-500 mb-1">RPwD Act 2016 Disability Category</label>
            <select
              value={rpwdCategory}
              onChange={(e) => setRpwdCategory(e.target.value)}
              className="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg font-bold text-slate-800"
            >
              {rpwd21Categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Goal Cards List */}
      <div className="space-y-4">
        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Active IEP SMART Goals for {activeStudent.name}</h3>
        <div className="space-y-3">
          {goals.map((goal) => (
            <div key={goal.id} className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 uppercase">
                    {goal.domain}
                  </span>
                  <h4 className="text-sm font-bold text-slate-900 mt-1">{goal.title}</h4>
                </div>
                <div className="text-right">
                  <div className="text-lg font-black text-emerald-700">{goal.progress}%</div>
                  <div className="text-[10px] text-slate-500">Target: {goal.targetDate}</div>
                </div>
              </div>

              {/* Progress Slider */}
              <div className="space-y-1">
                <div className="w-full bg-slate-200 rounded-full h-2.5 overflow-hidden">
                  <div className="bg-emerald-500 h-full rounded-full transition-all duration-500" style={{ width: `${goal.progress}%` }} />
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <button 
                    onClick={() => handleUpdateProgress(goal.id, -5)}
                    className="px-2 py-0.5 bg-white border border-slate-300 text-slate-700 text-[10px] font-bold rounded hover:bg-slate-100"
                  >
                    -5%
                  </button>
                  <button 
                    onClick={() => handleUpdateProgress(goal.id, 5)}
                    className="px-2 py-0.5 bg-emerald-600 text-white text-[10px] font-bold rounded hover:bg-emerald-700 shadow-sm"
                  >
                    +5%
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
